/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import qs from 'query-string'
import { isEmpty } from 'lodash'

import {
  TabbedView,
  FilterSidebar,
  Picklist,
  PicklistWithData,
  Cases,
  Spinner,
  SvgIcon
} from '../components'

import { CASE_TYPE_MAP_FILTER } from '../constants'

const getCaseHandlers = (responsibilities) => {
  let caseHandlers = {}
  caseHandlers['Anyone'] = ''
  responsibilities.edges.forEach((responsibility) => (
    caseHandlers[responsibility.node.identity.fullName] = responsibility.node.identity.id
  ))
  return caseHandlers
}

class CaseList extends React.Component {
  static propTypes = {
    affectsId: PropTypes.string,
    statusFilterInSidebar: PropTypes.bool,
    recordsPerPage: PropTypes.number,
    title: PropTypes.element,
    autoAssignToCaseHandler: PropTypes.bool
  }

  static defaultProps = {
    title: <React.Fragment>
        <SvgIcon
          variant='standard'
          symbol='case'
          colour='light'
          size='large'
          className='slds-m-right_small'
        />
        <div className='slds-m-top_medium u-font-size-x-large u-text-caps'>
          Cases
        </div>
      </React.Fragment>,
    autoAssignToCaseHandler: true
  }

  constructor(props) {
    super(props)

    // on remounting checks if data is still in props/store
    // - which will be the case navigating via 'Home' (global nav)
    let caseHandlers = props.data && props.data.responsibilities
      ? getCaseHandlers(props.data.responsibilities)
      : {}

    this.state = {
      statusFilter: props.statusFilterInSidebar ? null : 'open',
      productTypeFilter: '',
      requestTypeFilter: '',
      priorityFilter: '',
      createdAtFilter: '',
      nextActionAtFilter: '',
      assignedToFilter: '',
      createdByFilter: '',
      idleForFilter: '',
      reviewedFilter: 'No',
      startCreatedAt: '',
      endCreatedAt: '',
      startNextActionAt: '',
      endNextActionAt: '',
      idleForDate: '',
      orderBy: 'next_action_at',
      tab: 0,  // = open cases
      caseHandlers,
      search: ''
    }
  }

  componentDidMount() {
    if (this.props.location.search.length > 0) {
      // set filters according to query search params
      this.setFiltersFromQueryString(this.props.location.search)
    } else if (!isEmpty(this.state.caseHandlers)) {
      // if query string empty, initialise default state and reflect in query search params
      this.setDefaultStateAndUpdateUrl(this.props, this.state.caseHandlers)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.data.loading === true && this.props.data.loading === false) {
      if (Object.keys(this.state.caseHandlers).length === 0 && this.props.data.responsibilities) {
        let caseHandlers = getCaseHandlers(this.props.data.responsibilities)
        this.setState({ caseHandlers }, this.setDefaultStateAndUpdateUrl(this.props, caseHandlers))
      }
    } else if (this.props.data.loading === false && this.props.location.search === '') {
      this.setQueryString()  // reflect state in url search params
    }
  }

  setDefaultStateAndUpdateUrl = (props, caseHandlers) => {
    if (props.location.search === '') {
      let stateChange = Object.assign({}, { reviewedFilter: 'No' })
      let userUuid = localStorage.getItem('userUUID')
      let userIsCaseHandler = Object.values(caseHandlers).find(value => value === userUuid) !== undefined
      if (props.autoAssignToCaseHandler && userIsCaseHandler) {
        // autoAssignToCaseHandler set to false will override the default behaviour to set assignedTo state
        // & query variables to the user (only applicable if user is a case handler)
        if (this.state.statusFilter) {
          stateChange.assignedToFilter = userUuid
          stateChange.statusFilter = this.state.statusFilter
        } else {
          stateChange.assignedToFilter = userUuid
        }
      }

      this.setState(stateChange, () => this.setQueryString())
    }
  }

  setFiltersFromQueryString = (queryString) => {
    let queryParams = qs.parse(queryString)

    let filters = Object.assign(
      {},
      ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
        [ 'search', 'orderBy' ].indexOf(key) > -1
          ? { [key]: queryParams[key] }
          : { [`${key}Filter`]: queryParams[key] }
      ))
    )
    this.setState(filters)
  }

  setQueryString = () => {
    let { search, statusFilter, productTypeFilter, requestTypeFilter, priorityFilter, assignedToFilter,
      createdAtFilter, createdByFilter, nextActionAtFilter, idleForFilter, reviewedFilter, orderBy } = this.state

    let filters = {
      reviewed: reviewedFilter,
      orderBy
    }

    if (search !== '') {
      filters.search = search
    }

    if (statusFilter) {
      filters.status = statusFilter
    }

    if (['','all'].indexOf(productTypeFilter) === -1) {
      filters.productType = productTypeFilter
    }

    if (requestTypeFilter !== '') {
      filters.requestType = requestTypeFilter
    }

    if (['','all'].indexOf(priorityFilter) === -1) {
      filters.priority = priorityFilter
    }

    if (assignedToFilter !== '') {
      filters.assignedTo = assignedToFilter
    }

    if (createdAtFilter !== '') {
      filters.createdAt = createdAtFilter
    }

    if (createdByFilter !== '') {
      filters.createdBy = createdByFilter
    }

    if (nextActionAtFilter !== '') {
      filters.nextActionAt = nextActionAtFilter
    }

    if (idleForFilter !== '') {
      filters.idleFor = idleForFilter
    }

    this.props.history.push({ search: qs.stringify(filters) })
  }

  handleCreatedAtChoice = (createdAtChoice) => {
    let stateChange = null

    switch(createdAtChoice) {
      case 'More than 7 days ago':
        stateChange = {
          startCreatedAt: '',
          endCreatedAt: moment().subtract(7, 'days').utc(),
          createdAtFilter: createdAtChoice
        }
        break

      case 'Within last 7 days':
        stateChange = {
          startCreatedAt: moment().subtract(7, 'days').utc(),
          endCreatedAt: moment().add(1, 'days').utc(),
          createdAtFilter: createdAtChoice
        }
        break

      case 'Today':
        stateChange = {
          startCreatedAt: moment().startOf('day').utc(),
          endCreatedAt: moment().endOf('day').utc(),
          createdAtFilter: createdAtChoice
        }
        break

      default:
        stateChange = {
          startCreatedAt: '',
          endCreatedAt: '',
          createdAtFilter: createdAtChoice
        }
        break
    }

    this.setState(Object.assign(this.state, stateChange), () => this.setQueryString())
  }

  handleNextActionAtChoice = (nextActionAtChoice) => {
    let stateChange = null

    switch(nextActionAtChoice) {
      case 'More than 7 days ago':
        stateChange = {
          startNextActionAt: '',
          endNextActionAt: moment().subtract(7, 'days').utc(),
          nextActionAtFilter: nextActionAtChoice
        }
        break

      case 'Within last 7 days':
        stateChange = {
          startNextActionAt: moment().subtract(7, 'days').utc(),
          endNextActionAt: moment().add(1, 'days').utc(),
          nextActionAtFilter: nextActionAtChoice
        }
        break

      case 'Today':
        stateChange = {
          startNextActionAt: moment().startOf('day').utc(),
          endNextActionAt: moment().endOf('day').utc(),
          nextActionAtFilter: nextActionAtChoice
        }
        break

      case 'Tomorrow':
        stateChange = {
          startNextActionAt: moment().startOf('day').add(1, 'days').utc(),
          endNextActionAt: moment().endOf('day').add(1, 'days').utc(),
          nextActionAtFilter: nextActionAtChoice
        }
        break

      case 'This week':
        stateChange = {
          startNextActionAt: moment().startOf('week').utc(),
          endNextActionAt: moment().endOf('week').add(1, 'days').utc(),
          nextActionAtFilter: nextActionAtChoice
        }
        break

      case 'Next week':
        stateChange = {
          startNextActionAt: moment().add(7, 'days').utc(),
          endNextActionAt: moment().add(7, 'days').endOf('week').add(1, 'days').utc(),
          nextActionAtFilter: nextActionAtChoice
        }
        break

      default:
        stateChange = {
          startNextActionAt: '',
          endNextActionAt: '',
          nextActionAtFilter: nextActionAtChoice
        }
        break
    }

    this.setState(Object.assign(this.state, stateChange), () => this.setQueryString())
  }

  handleIdleForChoice = (idleForChoice) => {
    let stateChange = null

    switch(idleForChoice) {
      case 'For more than 14 days':
        stateChange = {
          idleForDate: moment().subtract(14, 'days').utc(),
          idleForFilter: idleForChoice
        }
        break

      case 'For more than 7 days':
        stateChange = {
          idleForDate: moment().subtract(7, 'days').utc(),
          idleForFilter: idleForChoice
        }
        break

      case 'For more than a day':
        stateChange = {
          idleForDate: moment().subtract(1, 'days').utc(),
          idleForFilter: idleForChoice
        }
        break

      default:
        stateChange = {
          idleForDate: '',
          idleForFilter: idleForChoice
        }
        break
    }

    this.setState(Object.assign(this.state, stateChange), () => this.setQueryString())
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.data.error) {
      return <div>Error occurred in attempting to retrieve case list</div>
    }

    let requestTypeChoices = CASE_TYPE_MAP_FILTER[this.state.productTypeFilter.toLowerCase()]
    let caseHandlers = this.state.caseHandlers
    let filters = [
      {
        title: 'Product type',
        options: <PicklistWithData
          id='productType'
          forField='CaseProductType'
          default='All'
          symbol='chevrondown'
          value={this.state.productTypeFilter}
          onChange={productType => {
            if (productType === 'all') {
              this.setState({
                productTypeFilter: '',
                requestTypeFilter: ''
              }, () => this.setQueryString())
            } else {
              this.setState({
                productTypeFilter: productType,
                requestTypeFilter: ''
              }, () => this.setQueryString())
            }
          }}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Request type',
        options: <Picklist
          id='requestType'
          options={requestTypeChoices}
          default='All'
          symbol='chevrondown'
          value={this.state.requestTypeFilter === ''
            ? 'All'
            : this.state.requestTypeFilter}
          onChange={requestType => this.setState({
            requestTypeFilter: (requestType === 'All' || requestType === '---') ? '' : requestType
          }, () => this.setQueryString())}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Priority',
        options: <PicklistWithData
          id='priority'
          forField='CasePriority'
          default='All'
          symbol='chevrondown'
          value={this.state.priorityFilter}
          onChange={priority => this.setState({
            priorityFilter: priority === 'all' ? '' : priority
          }, () => this.setQueryString())}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Created date',
        options: <Picklist
          id='createdDate'
          options={[
            'Any time', 'More than 7 days ago', 'Within last 7 days', 'Today'
          ]}
          default='Any time'
          symbol='chevrondown'
          value={this.state.createdAtFilter === ''
            ? 'Any time'
            : this.state.createdAtFilter
          }
          onChange={this.handleCreatedAtChoice}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Created by',
        options: <Picklist
          id='createdBy'
          options={Object.keys(caseHandlers)}
          default='Anyone'
          symbol='chevrondown'
          value={this.state.createdByFilter === ''
            ? 'Anyone'
            : Object.keys(caseHandlers).find(key => caseHandlers[key] === this.state.createdByFilter)
          }
          onChange={createdBy => (
            this.setState({
              createdByFilter: createdBy === ''
                ? createdBy
                : caseHandlers[createdBy]
            }, () => this.setQueryString())
          )}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Next action date',
        options: <Picklist
          id='nextActionDate'
          options={[
            'Any time', 'More than 7 days ago', 'Within last 7 days', 'Today', 'Tomorrow', 'This week', 'Next week'
          ]}
          default='Any time'
          symbol='chevrondown'
          value={this.state.nextActionAtFilter === ''
            ? 'Any time'
            : this.state.nextActionAtFilter
          }
          onChange={this.handleNextActionAtChoice}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Assigned to',
        options: <Picklist
          id='assignedTo'
          options={Object.keys(caseHandlers)}
          default='Anyone'
          symbol='chevrondown'
          value={this.state.assignedToFilter === ''
            ? 'Anyone'
            : Object.keys(caseHandlers).find(key => caseHandlers[key] === this.state.assignedToFilter)
          }
          onChange={assignedTo => (
            this.setState({
              assignedToFilter: assignedTo === ''
                ? assignedTo
                : caseHandlers[assignedTo]
            }, () => this.setQueryString())
          )}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Idle for',
        options: <Picklist
          id='idleFor'
          options={[
            'Any time', 'For more than 14 days', 'For more than 7 days', 'For more than a day'
          ]}
          default='Any time'
          symbol='chevrondown'
          value={this.state.idleForFilter === ''
            ? 'Any time'
            : this.state.idleForFilter}
          onChange={this.handleIdleForChoice}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Reviewed',
        options: <Picklist
          id='reviewed'
          options={[
            'No', 'Yes'
          ]}
          default='No'
          symbol='chevrondown'
          value={this.state.reviewedFilter}
          onChange={reviewed => (
            this.setState({
              reviewedFilter: reviewed
            }, () => this.setQueryString())
          )}
          closeDropdownOnSelect={true}
          inline={true}
        />
      }
    ]

    if (this.props.statusFilterInSidebar) {
      filters.unshift({
          title: 'Status',
          options: <Picklist
            id='status'
            options={['All', 'Open', 'Resolved']}
            default='all'
            symbol='chevrondown'
            value={this.state.statusFilter || ''}
            onChange={status => status === 'All'
              ? this.setState({ statusFilter: null }, () => this.setQueryString())
              : status === 'Open'
                ? this.setState({ statusFilter: 'open', orderBy: 'next_action_at'}, () => this.setQueryString())
                : this.setState({ statusFilter: 'resolved', orderBy: '-updated_at' }, () => this.setQueryString())
            }
            closeDropdownOnSelect={true}
            inline={true}
          />
        })
    }

    if (this.state.statusFilter === 'resolved') {
      filters.splice(3,1)
    }

    return (
      <div className="stage-main slds-grow slds-scrollable_y" role="main">
        <div className="slds-grid slds-wrap slds-size_1-of-1">
          <div className="tab-nav slds-size_1-of-1 u-bg-color-white listing-page-header">
            {this.props.title !== null && (
              <div className="listing-page-header_title slds-p-left_small slds-p-bottom_small">
                {this.props.title}
              </div>
            )}
            {this.props.statusFilterInSidebar ? null : (
              <TabbedView
                tabs={[
                  // <span className='slds-text-title_caps'>Overview</span>,  // don't yet know what this will need to show
                  <span className="slds-text-title_caps">Open</span>,
                  <span className="slds-text-title_caps">Resolved</span>,
                ]}
                contents={[]}
                selectedTab={this.state.statusFilter === "open" ? 0 : 1}
                onClick={(tab) => {
                  if (tab === 0) {
                    this.setState(
                      { statusFilter: "open", orderBy: "next_action_at" },
                      () => this.setQueryString()
                    );
                  } else {
                    this.setState(
                      { statusFilter: "resolved", orderBy: "-updated_at" },
                      () => this.setQueryString()
                    );
                  }
                }}
                tabsClassName="listing-page-header_tabs slds-align_absolute-center"
              />
            )}
          </div>
          <FilterSidebar
            filters={filters}
            combine={this.props.statusFilterInSidebar ? [1, 2] : [0, 1]}
            filtersApplied={
              [
                this.state.productTypeFilter,
                this.state.requestTypeFilter,
                this.state.priorityFilter,
                this.state.assignedToFilter,
                this.state.createdAtFilter,
                this.state.createdByFilter,
                this.state.nextActionAtFilter,
                this.state.idleForFilter,
                this.state.reviewedFilter === "No"
                  ? ""
                  : this.state.reviewedFilter,
              ].filter((filter) => filter.length > 0).length
            }
            onClear={() =>
              this.setState(
                {
                  productTypeFilter: "",
                  requestTypeFilter: "",
                  priorityFilter: "",
                  assignedToFilter: "",
                  createdAtFilter: "",
                  createdByFilter: "",
                  nextActionAtFilter: "",
                  idleForFilter: "",
                  idleForDate: "",
                  startCreatedAt: "",
                  endCreatedAt: "",
                  startNextActionAt: "",
                  endNextActionAt: "",
                  reviewedFilter: "No",
                },
                () => this.setQueryString()
              )
            }
            search={true}
            searchString={this.state.search}
            onSearchClick={(search) =>
              this.setState({ search }, () => this.setQueryString())
            }
            onSearchClear={() =>
              this.setState({ search: "" }, () => this.setQueryString())
            }
          />
          <div className="slds-col slds-size_10-of-12">
            <Cases
              status={this.state.statusFilter}
              productType={this.state.productTypeFilter}
              requestType={this.state.requestTypeFilter}
              priority={this.state.priorityFilter}
              assignedTo={this.state.assignedToFilter}
              createdBy={this.state.createdByFilter}
              startCreatedAt={
                this.state.startCreatedAt === ""
                  ? null
                  : this.state.startCreatedAt
                      .utc()
                      .format("YYYY-MM-DD HH:mm:ss.SSS")
              }
              endCreatedAt={
                this.state.endCreatedAt === ""
                  ? null
                  : this.state.endCreatedAt
                      .utc()
                      .format("YYYY-MM-DD HH:mm:ss.SSS")
              }
              startNextActionAt={
                this.state.startNextActionAt === ""
                  ? null
                  : this.state.startNextActionAt
                      .utc()
                      .format("YYYY-MM-DD HH:mm:ss.SSS")
              }
              endNextActionAt={
                this.state.endNextActionAt === ""
                  ? null
                  : this.state.endNextActionAt
                      .utc()
                      .format("YYYY-MM-DD HH:mm:ss.SSS")
              }
              idleForDate={
                this.state.idleForDate === ""
                  ? null
                  : this.state.idleForDate
                      .utc()
                      .format("YYYY-MM-DD HH:mm:ss.SSS")
              }
              search={this.state.search ? this.state.search : ""}
              reviewed={this.state.reviewedFilter}
              affects={this.props.affectsId}
              showStatusColumn={this.props.showStatusColumn}
              orderBy={this.state.orderBy}
              changeSort={(orderBy) =>
                this.setState({ orderBy }, () => this.setQueryString())
              }
              resultsPerPage={this.props.recordsPerPage}
            />
          </div>
        </div>
      </div>
    );
  }
}

const caseHandlerIdentities = gql`
  query {
    responsibilities(description_Icontains: "handle_cases", orderBy: "identity__first_name") {
      edges {
        node {
          id
          identity {
            id
            fullName
          }
        }
      }
    }
  }
`

const withData = graphql(caseHandlerIdentities)

export default compose(
  withData,
  withRouter
)(CaseList)



// WEBPACK FOOTER //
// ./src/containers/CaseList.js